import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, Tooltip, makeStyles } from "@material-ui/core";
import MouseIcon from "@material-ui/icons/Mouse";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import CloseIcon from "@material-ui/icons/Close";
import RoomContext from "../../context/RoomContext";
import { makePeerConsumerSelector } from "../../helpers/Selectors";
import * as roomActions from "../../actions/roomActions";
import {
  ROOM_MINI_TOGGLE_RESERVE_MINI_PX,
  ROOM_MINI_TOGGLE_RESERVE_PX,
  useConversationMiniToggleVisible,
} from "../../helpers/remoteControl/roomChrome";

const useStyles = makeStyles(() => ({
  root: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 40,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  rootReserveMiniToggle: {
    right: ROOM_MINI_TOGGLE_RESERVE_PX,
  },
  miniRoot: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 40,
  },
  miniRootReserveMiniToggle: {
    right: ROOM_MINI_TOGGLE_RESERVE_MINI_PX,
  },
  rootReserveWithFullscreenExit: {
    right: ROOM_MINI_TOGGLE_RESERVE_PX + 44,
  },
  miniRootReserveWithFullscreenExit: {
    right: ROOM_MINI_TOGGLE_RESERVE_MINI_PX + 36,
  },
  requestRoot: {
    position: "absolute",
    left: 10,
    top: 10,
    zIndex: 30,
  },
  miniRequestRoot: {
    position: "absolute",
    left: 6,
    bottom: 30,
    top: "auto",
    zIndex: 30,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: 4,
    borderRadius: 10,
    background: "rgba(24, 28, 30, 0.72)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.18)",
  },
  miniToolbar: {
    padding: 2,
    gap: 2,
    borderRadius: 6,
  },
  iconButton: {
    color: "#fff",
    padding: 6,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.12)",
    },
  },
  miniIconButton: {
    padding: 4,
  },
  stopButton: {
    color: "#ffb4b4",
    padding: 6,
    "&:hover": {
      background: "rgba(227, 59, 74, 0.18)",
    },
  },
  requestButton: {
    color: "#fff",
    minWidth: "auto",
    minHeight: "auto",
    padding: 4,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.12)",
    },
  },
  miniRequestButton: {
    padding: 2,
  },
  waitingLabel: {
    color: "rgba(255,255,255,0.9)",
    textTransform: "none",
    fontSize: 11,
    fontWeight: 500,
    padding: "2px 6px",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  miniWaitingLabel: {
    fontSize: 10,
    padding: "2px 4px",
  },
}));

const RemoteControlScreenActions = ({ hostPeerId }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { roomClient } = useContext(RoomContext);
  const meId = useSelector((state) => state.me.id);
  const isMini = useSelector((state) => state.call.displayMini);
  const reserveMiniToggle = useConversationMiniToggleVisible();
  const fullScreenConsumerId = useSelector(
    (state) => state.room.fullScreenConsumer
  );
  const session = useSelector((state) => state.remoteControl) || {
    status: "idle",
  };
  const host = useSelector((state) => state.peers[hostPeerId]);
  const getPeerConsumers = makePeerConsumerSelector();
  const { screenConsumer } = useSelector((state) =>
    getPeerConsumers(state, hostPeerId)
  );
  const canRequest = useSelector((state) => {
    const roles = state.me.roles || [];
    const perms = state.room.permissionsFromRoles || {};
    const allowed =
      perms.REQUEST_REMOTE_CONTROL && perms.REQUEST_REMOTE_CONTROL.length
        ? perms.REQUEST_REMOTE_CONTROL
        : perms.SHARE_SCREEN || [];

    return roles.some((role) => allowed.includes(role));
  });

  if (!hostPeerId || hostPeerId === meId) return null;

  const controllable = Boolean(
    host &&
      host.clientInfo &&
      host.clientInfo.capabilities &&
      host.clientInfo.capabilities.canBeControlled
  );

  const isController = session.controllerPeerId === meId;
  const isThisHost = session.hostPeerId === hostPeerId;
  const screenVisible =
    Boolean(screenConsumer) &&
    !screenConsumer.locallyPaused &&
    !screenConsumer.remotelyPaused;
  const isFullscreen =
    Boolean(screenConsumer) && fullScreenConsumerId === screenConsumer.id;
  const isActiveController =
    controllable &&
    session.status === "active" &&
    isThisHost &&
    isController;
  const showRightToolbar = isActiveController;
  const showFullscreenInToolbar = isActiveController && !isFullscreen;
  const showWaiting =
    controllable &&
    session.status === "requested" &&
    isThisHost &&
    isController;
  const showRequest =
    controllable && canRequest && session.status === "idle";

  if (!showRightToolbar && !showWaiting && !showRequest) return null;

  const topRightClass = [
    isMini ? classes.miniRoot : classes.root,
    isFullscreen
      ? isMini
        ? classes.miniRootReserveWithFullscreenExit
        : classes.rootReserveWithFullscreenExit
      : reserveMiniToggle
      ? isMini
        ? classes.miniRootReserveMiniToggle
        : classes.rootReserveMiniToggle
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  const leftRootClass = isMini ? classes.miniRequestRoot : classes.requestRoot;
  const toolbarClass = `${classes.toolbar} ${
    isMini ? classes.miniToolbar : ""
  }`;

  const toggleFullscreen = () => {
    if (screenConsumer) {
      dispatch(roomActions.toggleConsumerFullscreen(screenConsumer.id));
    }
  };

  return (
    <>
      {showWaiting && (
        <div className={leftRootClass}>
          <div className={toolbarClass}>
            <span
              className={`${classes.waitingLabel} ${
                isMini ? classes.miniWaitingLabel : ""
              }`}
            >
              <FormattedMessage
                id="remoteControl.waiting"
                defaultMessage="Waiting for approval"
              />
            </span>
          </div>
        </div>
      )}

      {showRequest && !showWaiting && (
        <div className={leftRootClass}>
          <div className={toolbarClass}>
            <Tooltip
              title={intl.formatMessage({
                id: "remoteControl.request",
                defaultMessage: "Request control",
              })}
            >
              <IconButton
                size="small"
                className={`${classes.requestButton} ${
                  isMini ? classes.miniRequestButton : ""
                }`}
                onClick={() => roomClient.requestRemoteControl(hostPeerId)}
              >
                <MouseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      {showRightToolbar && (
        <div className={topRightClass}>
          <div className={toolbarClass}>
            {showFullscreenInToolbar && (
              <Tooltip
                title={intl.formatMessage({
                  id: "label.fullscreen",
                  defaultMessage: "Fullscreen",
                })}
              >
                <span>
                  <IconButton
                    size="small"
                    className={`${classes.iconButton} ${
                      isMini ? classes.miniIconButton : ""
                    }`}
                    disabled={!screenVisible}
                    onClick={toggleFullscreen}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isActiveController && (
              <Tooltip
                title={intl.formatMessage({
                  id: "remoteControl.stop",
                  defaultMessage: "Stop control",
                })}
              >
                <IconButton
                  size="small"
                  className={`${classes.stopButton} ${
                    isMini ? classes.miniIconButton : ""
                  }`}
                  onClick={() => roomClient.endRemoteControl()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RemoteControlScreenActions;
